import React from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  LinearProgress,
  Typography,
} from '@mui/material';
import { redactConverterJobDiagnostics } from './converter-job-diagnostics';

const defaultLabels = {
  downloadDebug: 'Download debug report',
  progress: 'Job progress',
  technicalDetails: 'Technical details / log',
};

const ConverterJobRunPanel = ({
  errorMessage,
  isRunning,
  labels = {},
  onDownloadDebug,
  statusText,
  technicalEntries = [],
  uploadProgress,
}) => {
  const resolvedLabels = {
    ...defaultLabels,
    ...labels,
  };
  const hasError = typeof errorMessage === 'string'
    ? errorMessage.trim().length > 0
    : Boolean(errorMessage);
  const hasNumericProgress =
    typeof uploadProgress === 'number' &&
    Number.isFinite(uploadProgress) &&
    uploadProgress >= 0 &&
    uploadProgress <= 100;
  const shouldShowProgress = hasNumericProgress || isRunning;
  const safeTechnicalEntries = redactConverterJobDiagnostics(
    Array.isArray(technicalEntries) ? technicalEntries : [],
  );
  const hasTechnicalEntries = safeTechnicalEntries.length > 0;

  return (
    <Box sx={{ display: 'grid', gap: 1.5, mt: 2 }}>
      {statusText && (
        <Typography
          color="text.secondary"
          sx={{ overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}
          variant="body2"
        >
          {statusText}
        </Typography>
      )}

      {shouldShowProgress && (
        <LinearProgress
          aria-label={resolvedLabels.progress}
          value={hasNumericProgress ? uploadProgress : undefined}
          variant={hasNumericProgress ? 'determinate' : 'indeterminate'}
        />
      )}

      {hasError && (
        <Alert
          severity="error"
          sx={{
            '& .MuiAlert-icon': { py: 0.5 },
            '& .MuiAlert-message': {
              overflowWrap: 'anywhere',
              py: 0.5,
              whiteSpace: 'pre-wrap',
            },
            py: 0,
          }}
          variant="outlined"
        >
          {errorMessage}
        </Alert>
      )}

      {hasTechnicalEntries && (
        <Accordion
          defaultExpanded={false}
          disableGutters
          elevation={0}
          sx={{
            '&::before': { display: 'none' },
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ minHeight: 40 }}
          >
            <Typography variant="body2">
              {resolvedLabels.technicalDetails}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Box
              component="pre"
              sx={{
                backgroundColor: 'grey.100',
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: 12,
                m: 0,
                maxHeight: 240,
                overflow: 'auto',
                p: 1.5,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {JSON.stringify(safeTechnicalEntries, null, 2)}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {hasError && typeof onDownloadDebug === 'function' && (
        <Box>
          <Button
            color="error"
            onClick={onDownloadDebug}
            size="small"
            variant="outlined"
          >
            {resolvedLabels.downloadDebug}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ConverterJobRunPanel;
